import React, { useState } from 'react';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { CancellationObject, CurrencyObject } from 'common/types/common';
import { RiCloseLine, RiPencilLine } from 'react-icons/ri';
import { sortBy } from 'lodash';
import MoneyTextField from 'common/components/MoneyTextField';
import { constrain } from 'common/utils/math';
import { useTranslation } from 'services/localization';
import { cancellationHoursAsString } from 'common/utils/dateUtils';

export interface CancellationPolicyProps {
	value: CancellationObject[] | null | undefined;
	onChange: (value: CancellationObject[] | null) => void;
}
const CancellationPolicy = (props: CancellationPolicyProps) => {
	const { onChange } = props;
	const { t } = useTranslation();
	const value = getSortedTerms(props.value ?? []);

	const [editingTerm, setEditingTerm] = useState<{
		index: number;
		item: CancellationObject;
	} | null>(null);

	const handleEditHours = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!editingTerm) return;
		let value = Number(e.target.value);
		value = isNaN(value) ? 0 : value;

		setEditingTerm({
			...editingTerm,
			item: {
				...editingTerm.item,
				beforeHours: getUnitAsHours(value, editingTerm.item.displayAs || 'hours'),
			},
		});
	};

	const handleEditDisplayAs = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!editingTerm) return;
		const value = e.target.value as 'hours' | 'days' | 'weeks';

		setEditingTerm({
			...editingTerm,
			item: {
				...editingTerm.item,
				displayAs: value,
				beforeHours: convertTime(
					editingTerm.item.beforeHours || 0,
					editingTerm.item.displayAs || 'hours',
					value,
				),
			},
		});
	};

	const handleEditRefundPercentage = (value: number) => {
		if (!editingTerm) return;

		setEditingTerm({
			...editingTerm,
			item: {
				...editingTerm.item,
				refundPercentage: constrain(0, 1)(value / 100),
			},
		});
	};

	const handleDeleteRow = (index: number) => {
		const newValue = [...value];
		newValue.splice(index, 1);
		onChange(newValue);
	};

	const handleDialogCancel = () => {
		setEditingTerm(null);
	};

	const handleDialogDone = () => {
		if (!!editingTerm) {
			const newValue = [...value];
			const item = getValidCancellationObject(editingTerm.item);
			if (editingTerm.index === -1) {
				newValue.push(item);
			} else {
				newValue[editingTerm.index] = item;
			}
			onChange(newValue);
		}
		setEditingTerm(null);
	};

	return (
		<Box>
			<Typography variant="h6">
				{t('productSettings.cancellationPolicy', 'Cancellation policy')}
			</Typography>
			<Typography variant="body2" color="secondary">
				{t(
					'productSettings.cancellationPolicyDescription2',
					'Define your refund policy in case of cancelled orders.',
				)}
			</Typography>
			{value.length > 0 && (
				<Table sx={{ mt: 4 }}>
					<TableHead>
						<TableRow>
							<TableCell colSpan={2}>
								{t('common:cancellation.cancellationLatest', 'Cancel latest')}
							</TableCell>
							<TableCell colSpan={2} align="right">
								{t('common:payment.refund', 'Refund')}
							</TableCell>
							<TableCell></TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{value.map((cancellationObject, index) => (
							<React.Fragment>
								<TableRow>
									<TableCell colSpan={2}>
										{t('common:times.prior', {
											beforeHours: `${cancellationHoursAsString(cancellationObject, t)}`,
											defaultValue: '{{beforeHours}} prior',
										})}
									</TableCell>
									<TableCell colSpan={2} align="right">
										{(cancellationObject.refundPercentage || 0) * 100}%
									</TableCell>
									<TableCell align="right">
										<Stack direction="row" justifyContent="flex-end">
											<Tooltip title={t('common:actions.editRow', 'Edit row')}>
												<IconButton
													onClick={() => setEditingTerm({ index, item: cancellationObject })}
												>
													<RiPencilLine />
												</IconButton>
											</Tooltip>
											<Tooltip title={t('common:actions.deleteRow', 'Delete row')}>
												<IconButton onClick={() => handleDeleteRow(index)}>
													<RiCloseLine />
												</IconButton>
											</Tooltip>
										</Stack>
									</TableCell>
								</TableRow>
								{index === value.length - 1 && cancellationObject.beforeHours > 0 && (
									<TableRow>
										<TableCell colSpan={2}>
											{t('common:times.lessThan', {
												noCancellationHours: `${cancellationHoursAsString(cancellationObject, t)}`,
												defaultValue: 'Less than {{noCancellationHours}}',
											})}
										</TableCell>
										<TableCell colSpan={2} align="right">
											{t('common:cancellation.noRefund', 'No refund')}
										</TableCell>
										<TableCell></TableCell>
									</TableRow>
								)}
							</React.Fragment>
						))}
					</TableBody>
				</Table>
			)}

			<Stack direction="row" spacing={2} sx={{ mt: 4 }}>
				<Button
					variant="outlined"
					color="primary"
					onClick={() =>
						setEditingTerm({
							index: -1,
							item: { beforeHours: 0, displayAs: 'hours', refundPercentage: 0 },
						})
					}
				>
					+ {t('productSettings.newCancellationTerm', 'Add new cancellation term')}
				</Button>
			</Stack>

			<Dialog open={!!editingTerm}>
				<DialogTitle>
					{t('productSettings.editCancellationTerm', 'Edit cancellation term')}
				</DialogTitle>
				<DialogContent>
					<Stack direction="column" spacing={2}>
						<Typography>{t('common:cancellation.cancellationLatest', 'Cancel latest')}</Typography>
						<Stack direction="row" alignItems="center" spacing={2}>
							<TextField
								label="Time"
								value={getHoursAs(
									editingTerm?.item.beforeHours || 0,
									editingTerm?.item.displayAs ?? 'hours',
								)}
								onChange={handleEditHours}
								type="number"
							/>
							<TextField
								label="Unit"
								value={editingTerm?.item.displayAs ?? 'hours'}
								onChange={handleEditDisplayAs}
								select
							>
								<MenuItem value="hours">{t('common:times.hours', 'hours')}</MenuItem>
								<MenuItem value="days">{t('common:times.days', 'days')}</MenuItem>
								<MenuItem value="weeks">{t('common:times.weeks', 'weeks')}</MenuItem>
							</TextField>
							<Typography flex={1}>
								{t('productSettings.cancellationBeforeBooking2', 'before order')}
							</Typography>
						</Stack>
						<MoneyTextField
							label={t('common:payment.refund', 'Refund')}
							variant="outlined"
							value={100 * (editingTerm?.item.refundPercentage || 0)}
							onValueChange={handleEditRefundPercentage}
							currency={PERCENTAGE_CURRENCY}
						/>
					</Stack>
				</DialogContent>
				<DialogActions>
					<Button color="secondary" onClick={handleDialogCancel}>
						{t('common:actions.cancel')}
					</Button>
					<Button color="primary" variant="contained" onClick={handleDialogDone}>
						{t('common:actions.done')}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

const getSortedTerms = (terms: CancellationObject[]): CancellationObject[] => {
	return sortBy(terms, 'beforeHours').reverse();
};

const MULTIPLIERS = {
	hours: 1,
	days: 24,
	weeks: 24 * 7,
};

const getHoursAs = (hours: number, unit: 'hours' | 'days' | 'weeks') => {
	return Math.round(hours / MULTIPLIERS[unit]);
};

const getUnitAsHours = (amount: number, unit: 'hours' | 'days' | 'weeks') => {
	return amount * MULTIPLIERS[unit];
};

const convertTime = (
	amount: number,
	from: 'hours' | 'days' | 'weeks',
	to: 'hours' | 'days' | 'weeks',
) => {
	return getUnitAsHours(getHoursAs(amount, from), to);
};

const getValidCancellationObject = (term: CancellationObject): CancellationObject => {
	return {
		beforeHours: term.beforeHours || 0,
		displayAs: term.displayAs || 'hours',
		refundPercentage: term.refundPercentage || 0,
	};
};

const PERCENTAGE_CURRENCY: CurrencyObject = {
	code: '%',
	displayAs: '%',
	position: 'suffix',
};

export default CancellationPolicy;
